import { c as _c } from "react/compiler-runtime";
import { BookingQualificationProjectSummary } from "@justhome/api-core";
import { formatMoney, formatPropertyType } from "@justhome/common";
import { Icon, IconType, Skeleton } from "@justhome/react-ui";
import useBookingTranslation from "@modules/booking/lib/hooks/useBookingTranslation";
import Image from "next/image";
import { FC } from "react";
interface PropertyCardProps {
  project: BookingQualificationProjectSummary;
}
const PropertyImage = t0 => {
  const $ = _c(2);
  const {
    imageUrl
  } = t0;
  let t1;
  if ($[0] !== imageUrl) {
    t1 = <Skeleton width="100%" height="100%" className="absolute inset-0 rounded-none" isLoading={false}><div className="relative h-full w-full">{imageUrl ? <Image loader={_temp} src={imageUrl} fill={true} sizes="220px" alt="" className="object-cover" /> : <div className="bg-neutral-12 h-full flex items-center justify-center"><Icon type="House" className="text-neutral-150" size="48" /></div>}</div></Skeleton>;
    $[0] = imageUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PropertyMainInfo = t0 => {
  const $ = _c(8);
  const {
    street,
    postalCode,
    city
  } = t0;
  let t1;
  if ($[0] !== street) {
    t1 = street && <h3 className="text-app-body-1 mb-xs">{street}</h3>;
    $[0] = street;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== city || $[3] !== postalCode) {
    t2 = <p className="text-app-body-1 text-neutral-425">{postalCode} {city}</p>;
    $[2] = city;
    $[3] = postalCode;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const PropertyDetailsRow: FC<{
  label: string;
  value: string;
  icon: IconType;
}> = t0 => {
  const $ = _c(12);
  const {
    label,
    value,
    icon
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <Icon type={icon} className="text-primary-800" size="16" />;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-neutral-675/60">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <dt className="flex gap-2xs items-center">{t1}{t2}</dt>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== value) {
    t4 = <dd className="text-right flex-1">{value}</dd>;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <dl className="flex items-center justify-between text-title-1 m:text-body-2 gap-2xs flex-wrap">{t3}{t4}</dl>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const PropertyCard = t0 => {
  const $ = _c(27);
  const {
    project
  } = t0;
  const {
    t,
    i18n
  } = useBookingTranslation();
  let t1;
  if ($[0] !== project.imageUrl) {
    t1 = <div className="hidden m:block w-[214px] flex-none relative"><PropertyImage imageUrl={project.imageUrl} /></div>;
    $[0] = project.imageUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== project.city || $[3] !== project.postalCode || $[4] !== project.street) {
    t2 = <PropertyMainInfo street={project.street} postalCode={project.postalCode} city={project.city} />;
    $[2] = project.city;
    $[3] = project.postalCode;
    $[4] = project.street;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== i18n || $[7] !== project.purchasePrice || $[8] !== t) {
    t3 = project.purchasePrice !== undefined && <PropertyDetailsRow label={t("projectConfirmation.purchasePriceLabel")} value={formatMoney(project.purchasePrice, true, i18n.language)} icon="Euro" />;
    $[6] = i18n;
    $[7] = project.purchasePrice;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== project.livingArea || $[11] !== t) {
    t4 = !!project.livingArea && <PropertyDetailsRow label={t("projectConfirmation.livingAreaLabel")} value={`${project.livingArea} m²`} icon="SquareMeter" />;
    $[10] = project.livingArea;
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== i18n || $[14] !== project.propertyType || $[15] !== t) {
    t5 = project.propertyType && <PropertyDetailsRow label={t("projectConfirmation.propertyTypeLabel")} value={formatPropertyType(project.propertyType, i18n.language)} icon="House" />;
    $[13] = i18n;
    $[14] = project.propertyType;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <div className="flex flex-col gap-xs">{t3}{t4}{t5}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t2 || $[22] !== t6) {
    t7 = <div className="w-full p-3xs m:p-s flex flex-col gap-s justify-between">{t2}{t6}</div>;
    $[21] = t2;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t1 || $[25] !== t7) {
    t8 = <section className="rounded-2xs bg-neutral-0 border border-neutral-50 flex overflow-hidden">{t1}{t7}</section>;
    $[24] = t1;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
export default PropertyCard;
function _temp(t0) {
  const {
    src
  } = t0;
  return src;
}